import React, { useRef, useEffect, useState, useCallback } from 'react'
import { LAYER_META } from '../data/injuries'

//  CDN loaders 
const THREE_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js'
const GLTF_CDN  = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js'

let _threePromise = null
function getThree() {
  if (window.THREE && window.THREE.GLTFLoader) return Promise.resolve(window.THREE)
  if (_threePromise) return _threePromise
  _threePromise = new Promise((resolve, reject) => {
    function loadScript(src, onload) {
      const s = document.createElement('script')
      s.src = src; s.crossOrigin = 'anonymous'
      s.onload = onload
      s.onerror = () => reject(new Error('Failed to load: ' + src))
      document.head.appendChild(s)
    }
    loadScript(THREE_CDN, () => loadScript(GLTF_CDN, () => resolve(window.THREE)))
  })
  return _threePromise
}

// GLB world space (after root-matrix 90° X rotation): Y=0 body center,
// Y≈+1.5 head crown, Y≈-1.5 feet. autoScale≈0.04401, center3Y≈-9.054.
const SCALE = 0.04401   // fallback autoScale if GLB load fails

//  Region bounding boxes (highlight overlay, in OBJ coordinate space) 
const REGION_BOXES = {
  'Head / Neck':        { xMin:-2.2, xMax: 2.2, yMin:16.5, yMax:21.0, zMin:-2,   zMax:2   },
  'Left Shoulder':      { xMin:-6.0, xMax:-1.5, yMin:13.5, yMax:18.5, zMin:-2,   zMax:2   },
  'Right Shoulder':     { xMin: 1.5, xMax: 6.0, yMin:13.5, yMax:18.5, zMin:-2,   zMax:2   },
  'Chest / Ribs':       { xMin:-2.2, xMax: 2.2, yMin:12.0, yMax:17.0, zMin:-0.2, zMax:2.0 },
  'Upper Back':         { xMin:-2.2, xMax: 2.2, yMin:14.0, yMax:18.5, zMin:-2.0, zMax:0.2 },
  'Mid Back':           { xMin:-2.2, xMax: 2.2, yMin:11.0, yMax:15.5, zMin:-2.0, zMax:0.2 },
  'Lower Back':         { xMin:-2.2, xMax: 2.2, yMin: 8.0, yMax:12.5, zMin:-2.0, zMax:0.2 },
  'Abdomen / Groin':    { xMin:-2.2, xMax: 2.2, yMin: 8.5, yMax:14.0, zMin:-0.2, zMax:2.0 },
  'Left Elbow':         { xMin:-6.2, xMax:-3.8, yMin: 9.5, yMax:13.0, zMin:-2,   zMax:2   },
  'Right Elbow':        { xMin: 3.8, xMax: 6.2, yMin: 9.5, yMax:13.0, zMin:-2,   zMax:2   },
  'Left Wrist / Hand':  { xMin:-6.2, xMax:-4.0, yMin: 6.5, yMax:10.0, zMin:-2,   zMax:2   },
  'Right Wrist / Hand': { xMin: 4.0, xMax: 6.2, yMin: 6.5, yMax:10.0, zMin:-2,   zMax:2   },
  'Left Hip':           { xMin:-2.8, xMax:-0.1, yMin: 8.5, yMax:12.5, zMin:-2,   zMax:2   },
  'Right Hip':          { xMin: 0.1, xMax: 2.8, yMin: 8.5, yMax:12.5, zMin:-2,   zMax:2   },
  'Left Knee':          { xMin:-2.0, xMax:-0.1, yMin: 4.8, yMax: 7.2, zMin:-2,   zMax:2   },
  'Right Knee':         { xMin: 0.1, xMax: 2.0, yMin: 4.8, yMax: 7.2, zMin:-2,   zMax:2   },
  'Left Ankle / Foot':  { xMin:-2.0, xMax:-0.1, yMin:-0.5, yMax: 3.0, zMin:-2,   zMax:2   },
  'Right Ankle / Foot': { xMin: 0.1, xMax: 2.0, yMin:-0.5, yMax: 3.0, zMin:-2,   zMax:2   },
}

//  Per-injury precise highlight boxes (OBJ coordinate space) 
// Overrides broad REGION_BOXES for accuracy. Bilateral injuries list both sides.
// B(x1,x2,y1,y2,z1,z2) shorthand used inline below.
const B = (x1,x2,y1,y2,z1,z2) => ({ xMin:x1, xMax:x2, yMin:y1, yMax:y2, zMin:z1, zMax:z2 })

const INJURY_BOXES = {
  //  KNEE (tight joint box) 
  acl_tear:             [ B(-1.8,-0.1, 5.0,7.0,-1.5,1.5), B(0.1,1.8,  5.0,7.0,-1.5,1.5) ],
  pcl_tear:             [ B(-1.8,-0.1, 5.0,7.0,-1.5,0.5), B(0.1,1.8,  5.0,7.0,-1.5,0.5) ],
  mcl_tear:             [ B(-1.0,-0.1, 4.8,7.2,-1.0,1.0), B(0.1,1.0,  4.8,7.2,-1.0,1.0) ],   // medial
  lcl_tear:             [ B(-2.0,-1.0, 4.8,7.2,-1.0,1.0), B(1.0,2.0,  4.8,7.2,-1.0,1.0) ],   // lateral
  meniscus_tear:        [ B(-1.8,-0.1, 5.0,6.8,-1.5,1.5), B(0.1,1.8,  5.0,6.8,-1.5,1.5) ],
  runners_knee:         [ B(-1.8,-0.1, 5.2,7.5,-0.5,2.0), B(0.1,1.8,  5.2,7.5,-0.5,2.0) ],   // front of knee
  patellar_tendinitis:  [ B(-1.5,-0.1, 5.0,7.2, 0.2,2.0), B(0.1,1.5,  5.0,7.2, 0.2,2.0) ],   // front/patellar
  // IT Band — lateral thigh all the way from hip to knee
  it_band_syndrome:     [ B(-2.5,-0.8, 4.2,11.5,-1.5,1.5), B(0.8,2.5, 4.2,11.5,-1.5,1.5) ],
  knee_bursitis:        [ B(-1.6,-0.1, 5.5,7.5, 0.0,2.0), B(0.1,1.6,  5.5,7.5, 0.0,2.0) ],
  chondromalacia:       [ B(-1.6,-0.1, 5.5,7.2,-0.5,2.0), B(0.1,1.6,  5.5,7.2,-0.5,2.0) ],
  knee_oa:              [ B(-1.8,-0.1, 4.8,7.2,-1.8,1.8), B(0.1,1.8,  4.8,7.2,-1.8,1.8) ],
  quad_tendon_rupture:  [ B(-1.6,-0.1, 5.8,8.5, 0.2,2.0), B(0.1,1.6,  5.8,8.5, 0.2,2.0) ],
  osgood_schlatter:     [ B(-1.4,-0.1, 4.8,6.5, 0.2,2.0), B(0.1,1.4,  4.8,6.5, 0.2,2.0) ],
  post_acl:             [ B(-1.8,-0.1, 4.8,7.2,-1.5,1.5), B(0.1,1.8,  4.8,7.2,-1.5,1.5) ],
  post_knee_replacement:[ B(-1.8,-0.1, 4.8,7.2,-1.8,1.8), B(0.1,1.8,  4.8,7.2,-1.8,1.8) ],

  //  SHOULDER 
  rotator_cuff_tear:    [ B(-5.5,-1.5, 14.0,18.0,-2,2), B(1.5,5.5, 14.0,18.0,-2,2) ],
  shoulder_impingement: [ B(-5.0,-1.5, 15.0,18.5,-1,2), B(1.5,5.0, 15.0,18.5,-1,2) ],
  frozen_shoulder:      [ B(-5.5,-1.5, 14.0,18.0,-2,2), B(1.5,5.5, 14.0,18.0,-2,2) ],
  slap_tear:            [ B(-5.0,-1.5, 14.5,18.0,-2,2), B(1.5,5.0, 14.5,18.0,-2,2) ],
  shoulder_dislocation: [ B(-5.5,-1.5, 14.0,18.5,-2,2), B(1.5,5.5, 14.0,18.5,-2,2) ],
  ac_separation:        [ B(-4.5,-1.5, 16.0,18.5,-1,1), B(1.5,4.5, 16.0,18.5,-1,1) ],
  shoulder_bursitis:    [ B(-5.0,-1.5, 15.0,18.5,-1,2), B(1.5,5.0, 15.0,18.5,-1,2) ],
  biceps_tendinitis:    [ B(-5.0,-1.5, 14.0,18.5, 0,2), B(1.5,5.0, 14.0,18.5, 0,2) ],
  shoulder_instability: [ B(-5.5,-1.5, 14.0,18.5,-2,2), B(1.5,5.5, 14.0,18.5,-2,2) ],
  post_shoulder_replacement: [ B(-5.5,-1.5, 14.0,18.0,-2,2), B(1.5,5.5, 14.0,18.0,-2,2) ],
  thoracic_outlet:      [ B(-5.0,-1.0, 15.0,19.5,-2,2), B(1.0,5.0, 15.0,19.5,-2,2) ],
  calcific_tendinitis:  [ B(-5.0,-1.5, 15.5,18.5,-1,2), B(1.5,5.0, 15.5,18.5,-1,2) ],

  //  LOWER BACK 
  lumbar_herniated_disc:[ B(-2.0,2.0, 8.5,12.0,-2.0,0.2) ],
  lumbar_stenosis:      [ B(-2.0,2.0, 8.5,12.5,-2.0,0.2) ],
  si_joint:             [ B(-2.2,2.2, 8.0,11.5,-2.0,0.2) ],
  lumbar_strain:        [ B(-2.2,2.2, 8.5,13.0,-2.0,0.2) ],
  spondylolisthesis:    [ B(-1.8,1.8, 9.0,12.5,-2.0,0.2) ],
  ddd_lumbar:           [ B(-2.0,2.0, 8.0,12.5,-2.0,0.2) ],
  sciatica:             [ B(-2.2,2.2, 7.5,13.0,-2.0,0.5) ],
  piriformis_syndrome:  [ B(-2.5,-0.1, 8.5,12.0,-2.0,0.2), B(0.1,2.5, 8.5,12.0,-2.0,0.2) ],
  lumbar_radiculopathy: [ B(-2.0,2.0, 8.0,13.0,-2.0,0.2) ],
  post_spinal_fusion:   [ B(-2.0,2.0, 8.5,12.5,-2.0,0.2) ],
  facet_syndrome:       [ B(-2.0,2.0, 8.5,12.5,-2.0,0.2) ],
  scoliosis:            [ B(-2.2,2.2, 8.0,17.0,-2.0,0.2) ],

  //  NECK 
  cervical_herniated_disc: [ B(-1.5,1.5, 16.5,19.5,-2,2) ],
  cervical_stenosis:    [ B(-1.5,1.5, 16.5,19.5,-2,2) ],
  whiplash:             [ B(-2.0,2.0, 16.0,20.0,-2,2) ],
  cervical_strain:      [ B(-2.0,2.0, 16.0,20.0,-2,2) ],
  cervical_radiculopathy: [ B(-2.0,2.0, 16.0,20.0,-2,2) ],
  torticollis:          [ B(-2.5,2.5, 16.5,20.5,-2,2) ],
  text_neck:            [ B(-2.0,2.0, 15.5,20.5,-2,2) ],

  //  HIP 
  hip_labrum_tear:      [ B(-2.5,-0.1, 9.0,12.0,-2,2), B(0.1,2.5, 9.0,12.0,-2,2) ],
  fai:                  [ B(-2.5,-0.1, 9.0,12.5,-2,2), B(0.1,2.5, 9.0,12.5,-2,2) ],
  hip_bursitis:         [ B(-2.8,-1.0, 9.0,12.5,-1.5,1.5), B(1.0,2.8, 9.0,12.5,-1.5,1.5) ],   // lateral trochanter
  hip_flexor_strain:    [ B(-2.2,-0.1, 9.5,13.0, 0.0,2.0), B(0.1,2.2, 9.5,13.0, 0.0,2.0) ],   // front hip
  gluteal_tendinopathy: [ B(-2.8,-0.1, 9.0,12.5,-2.0,0.0), B(0.1,2.8, 9.0,12.5,-2.0,0.0) ],   // posterior
  hip_oa:               [ B(-2.5,-0.1, 8.5,12.5,-2,2), B(0.1,2.5, 8.5,12.5,-2,2) ],
  post_hip_replacement: [ B(-2.5,-0.1, 8.5,12.5,-2,2), B(0.1,2.5, 8.5,12.5,-2,2) ],
  hip_pointer:          [ B(-2.8,-0.5, 10.0,13.5,-2,2), B(0.5,2.8, 10.0,13.5,-2,2) ],

  //  ANKLE / FOOT 
  ankle_sprain_mild:    [ B(-2.0,-0.1, 0.5,2.8,-2,2), B(0.1,2.0, 0.5,2.8,-2,2) ],
  ankle_sprain_severe:  [ B(-2.0,-0.1, 0.5,2.8,-2,2), B(0.1,2.0, 0.5,2.8,-2,2) ],
  // Achilles — back of heel/lower ankle only
  achilles_tendinitis:  [ B(-1.8,-0.1, 0.5,3.5,-2.0,0.2), B(0.1,1.8, 0.5,3.5,-2.0,0.2) ],
  achilles_rupture:     [ B(-1.8,-0.1, 0.5,3.5,-2.0,0.2), B(0.1,1.8, 0.5,3.5,-2.0,0.2) ],
  // Plantar fascia — sole of foot
  plantar_fasciitis:    [ B(-1.8,-0.1,-0.5,1.5,-0.5,2.5), B(0.1,1.8,-0.5,1.5,-0.5,2.5) ],
  pttd:                 [ B(-2.0,-0.1, 0.5,4.0,-1.5,1.5), B(0.1,2.0, 0.5,4.0,-1.5,1.5) ],
  peroneal_tendinitis:  [ B(-2.0,-0.8, 0.5,4.5,-2,2), B(0.8,2.0, 0.5,4.5,-2,2) ],   // lateral ankle
  ankle_impingement:    [ B(-2.0,-0.1, 0.5,3.0,-2,2), B(0.1,2.0, 0.5,3.0,-2,2) ],
  stress_fracture_foot: [ B(-1.8,-0.1,-0.5,2.5,-1,2.5), B(0.1,1.8,-0.5,2.5,-1,2.5) ],
  mortons_neuroma:      [ B(-1.5,-0.1,-0.5,1.2,-0.5,2.5), B(0.1,1.5,-0.5,1.2,-0.5,2.5) ],

  //  ELBOW 
  tennis_elbow:         [ B(-6.2,-3.5, 9.5,12.5, 0,2),  B(3.5,6.2, 9.5,12.5, 0,2)  ],   // lateral epicondyle
  golfers_elbow:        [ B(-6.2,-3.5, 9.5,12.5,-2,0),  B(3.5,6.2, 9.5,12.5,-2,0)  ],   // medial epicondyle
  ulnar_nerve:          [ B(-6.2,-3.5, 9.5,13.0,-2,0.5),B(3.5,6.2, 9.5,13.0,-2,0.5)],
  elbow_bursitis:       [ B(-6.2,-3.5, 9.5,12.5,-2,2),  B(3.5,6.2, 9.5,12.5,-2,2)  ],
  cubital_tunnel:       [ B(-6.2,-3.5, 9.5,13.0,-2,0.5),B(3.5,6.2, 9.5,13.0,-2,0.5)],

  //  WRIST / HAND 
  carpal_tunnel:        [ B(-6.2,-3.8, 6.5,9.5,-2,2), B(3.8,6.2, 6.5,9.5,-2,2) ],
  wrist_sprain:         [ B(-6.2,-3.8, 7.0,9.5,-2,2), B(3.8,6.2, 7.0,9.5,-2,2) ],
  de_quervain:          [ B(-6.2,-3.8, 7.5,10.5,-2,2),B(3.8,6.2, 7.5,10.5,-2,2)],
  trigger_finger:       [ B(-6.2,-3.8, 6.0,9.5,-2,2), B(3.8,6.2, 6.0,9.5,-2,2) ],
  dupuytren:            [ B(-6.2,-3.8, 6.0,9.5,-2,2), B(3.8,6.2, 6.0,9.5,-2,2) ],
  crps:                 [ B(-6.2,-3.5, 6.0,10.0,-2,2),B(3.5,6.2, 6.0,10.0,-2,2)],

  //  UPPER / MID BACK 
  thoracic_herniated_disc: [ B(-2.0,2.0, 11.0,15.5,-2.0,0.2) ],
  postural_dysfunction: [ B(-2.2,2.2, 11.0,18.5,-2.0,0.2) ],
  kyphosis:             [ B(-2.2,2.2, 11.0,18.0,-2.0,0.2) ],
  rib_stress_fracture:  [ B(-2.2,2.2, 12.0,17.0,-2.0,2.0) ],
  costochondritis:      [ B(-2.2,2.2, 12.0,17.0,-0.2,2.0) ],

  //  ABDOMEN 
  sports_hernia:        [ B(-2.2,2.2, 8.5,12.0, 0.0,2.0) ],
  pelvic_floor:         [ B(-2.2,2.2, 8.0,11.0,-1.5,2.0) ],
  diastasis_recti:      [ B(-2.0,2.0, 10.0,15.0, 0.2,2.0) ],

  //  HEAD / NEUROLOGICAL 
  concussion:           [ B(-2.2,2.2, 17.5,21.0,-2,2) ],
  vestibular_disorder:  [ B(-2.2,2.2, 17.5,21.0,-2,2) ],
  tmj:                  [ B(-2.5,2.5, 18.0,20.5,-1,2) ],
  stroke_rehab:         [ B(-2.5,2.5, 16.0,21.0,-2,2) ],
  parkinsons:           [ B(-2.5,2.5, 16.0,21.0,-2,2) ],
  brachial_plexus:      [ B(-4.0,4.0, 16.0,20.0,-2,2) ],
  // Bilateral — legs and arms
  peripheral_neuropathy:[ B(-2.0,-0.1,-0.5,4.0,-2,2), B(0.1,2.0,-0.5,4.0,-2,2) ],
}

// Return bounding boxes for an injury — per-injury precise first, fallback to region boxes
function getInjuryBoxes(injury) {
  if (!injury) return []
  const precise = INJURY_BOXES[injury.id]
  if (precise) return precise
  return (injury.bodyRegions || []).map(r => REGION_BOXES[r]).filter(Boolean)
}

//  Pain dot positions in WORLD space 
// Model is always auto-scaled to exactly 3 units tall, vertically centered at Y=0.
// World coords: Y=0 = body center, Y≈+1.5 = head crown, Y≈-1.5 = feet
// Verified against actual GLB vertex data (autoScale=0.04401, center3Y=-9.054):
//   Knee leg center X ≈ ±0.19, outer ≈ ±0.26
//   Ankle leg center X ≈ ±0.20, outer ≈ ±0.26
//   Arm center X ≈ ±0.53 (elbow/wrist range)
//   Hip outer X ≈ ±0.58, joint X ≈ ±0.25
//   Shoulder outer X ≈ ±0.45
// Z: 0=mid-depth, +front, −back (body front ≈+0.10, spine ≈-0.12)
// Dots are converted to model-local coords at placement time so they rotate with model.
// pw = pain world marker
const pw = (wx, wy, wz) => [wx, wy, wz]

const PAIN_POINTS = {
  //  KNEE  (world Y≈-0.67, leg center X≈±0.19, outer X≈±0.26) 
  acl_tear:              [ pw(-0.19,-0.67, 0.03), pw( 0.19,-0.67, 0.03) ],
  pcl_tear:              [ pw(-0.19,-0.67,-0.10), pw( 0.19,-0.67,-0.10) ],
  mcl_tear:              [ pw(-0.14,-0.67, 0.02), pw( 0.14,-0.67, 0.02) ],  // medial (inner) side
  lcl_tear:              [ pw(-0.24,-0.67, 0.02), pw( 0.24,-0.67, 0.02) ],  // lateral (outer) side
  meniscus_tear:         [ pw(-0.19,-0.68, 0.01), pw( 0.19,-0.68, 0.01) ],
  runners_knee:          [ pw(-0.19,-0.60, 0.12), pw( 0.19,-0.60, 0.12) ],  // front of knee
  patellar_tendinitis:   [ pw(-0.19,-0.71, 0.11), pw( 0.19,-0.71, 0.11) ],  // just below patella
  it_band_syndrome:      [ pw(-0.24,-0.67, 0.02), pw( 0.24,-0.67, 0.02) ],  // lateral knee
  knee_bursitis:         [ pw(-0.19,-0.64, 0.12), pw( 0.19,-0.64, 0.12) ],
  chondromalacia:        [ pw(-0.19,-0.62, 0.10), pw( 0.19,-0.62, 0.10) ],  // under patella
  knee_oa:               [ pw(-0.19,-0.67, 0.01), pw( 0.19,-0.67, 0.01) ],
  quad_tendon_rupture:   [ pw(-0.19,-0.56, 0.10), pw( 0.19,-0.56, 0.10) ],  // above kneecap
  osgood_schlatter:      [ pw(-0.19,-0.74, 0.10), pw( 0.19,-0.74, 0.10) ],  // tibial tuberosity
  post_acl:              [ pw(-0.19,-0.67, 0.03), pw( 0.19,-0.67, 0.03) ],
  post_knee_replacement: [ pw(-0.19,-0.67, 0.01), pw( 0.19,-0.67, 0.01) ],

  //  SHOULDER  (world Y≈+0.82) 
  rotator_cuff_tear:         [ pw(-0.50, 0.82, 0.03), pw( 0.50, 0.82, 0.03) ],
  shoulder_impingement:      [ pw(-0.50, 0.85, 0.05), pw( 0.50, 0.85, 0.05) ],
  frozen_shoulder:           [ pw(-0.50, 0.82, 0.02), pw( 0.50, 0.82, 0.02) ],
  slap_tear:                 [ pw(-0.48, 0.82,-0.02), pw( 0.48, 0.82,-0.02) ],  // deep glenohumeral
  shoulder_dislocation:      [ pw(-0.50, 0.82, 0.03), pw( 0.50, 0.82, 0.03) ],
  ac_separation:             [ pw(-0.45, 0.91, 0.02), pw( 0.45, 0.91, 0.02) ],  // top of shoulder
  shoulder_bursitis:         [ pw(-0.50, 0.85, 0.04), pw( 0.50, 0.85, 0.04) ],
  biceps_tendinitis:         [ pw(-0.48, 0.82, 0.09), pw( 0.48, 0.82, 0.09) ],  // bicipital groove (front)
  shoulder_instability:      [ pw(-0.50, 0.82, 0.02), pw( 0.50, 0.82, 0.02) ],
  post_shoulder_replacement: [ pw(-0.50, 0.82, 0.02), pw( 0.50, 0.82, 0.02) ],
  thoracic_outlet:           [ pw(-0.30, 0.97, 0.03), pw( 0.30, 0.97, 0.03) ],  // neck-shoulder junction
  calcific_tendinitis:       [ pw(-0.50, 0.85, 0.03), pw( 0.50, 0.85, 0.03) ],

  //  LOWER BACK  (world Y≈0.0–0.10) 
  lumbar_herniated_disc: [ pw( 0.00, 0.05,-0.13) ],
  lumbar_stenosis:       [ pw( 0.00, 0.05,-0.13) ],
  si_joint:              [ pw(-0.11, 0.00,-0.13), pw( 0.11, 0.00,-0.13) ],
  lumbar_strain:         [ pw( 0.00, 0.10,-0.13) ],
  spondylolisthesis:     [ pw( 0.00, 0.05,-0.14) ],
  ddd_lumbar:            [ pw( 0.00, 0.05,-0.13) ],
  sciatica:              [ pw(-0.08, 0.03,-0.13), pw( 0.08, 0.03,-0.13) ],
  piriformis_syndrome:   [ pw(-0.15,-0.02,-0.14), pw( 0.15,-0.02,-0.14) ],
  lumbar_radiculopathy:  [ pw( 0.00, 0.02,-0.13) ],
  post_spinal_fusion:    [ pw( 0.00, 0.05,-0.13) ],
  facet_syndrome:        [ pw(-0.06, 0.05,-0.16), pw( 0.06, 0.05,-0.16) ],
  scoliosis:             [ pw( 0.00, 0.28,-0.13) ],  // mid-spine

  //  NECK  (world Y≈1.05–1.12) 
  cervical_herniated_disc: [ pw( 0.00, 1.10,-0.08) ],
  cervical_stenosis:       [ pw( 0.00, 1.10,-0.08) ],
  whiplash:                [ pw( 0.00, 1.08, 0.00) ],
  cervical_strain:         [ pw( 0.00, 1.08,-0.07) ],
  cervical_radiculopathy:  [ pw(-0.09, 1.08,-0.05), pw( 0.09, 1.08,-0.05) ],
  torticollis:             [ pw(-0.11, 1.10, 0.00), pw( 0.11, 1.10, 0.00) ],
  text_neck:               [ pw( 0.00, 1.05, 0.07) ],  // forward head

  //  HIP  (world Y≈-0.04) 
  hip_labrum_tear:      [ pw(-0.23,-0.04, 0.11), pw( 0.23,-0.04, 0.11) ],  // front of joint
  fai:                  [ pw(-0.23,-0.03, 0.11), pw( 0.23,-0.03, 0.11) ],
  hip_bursitis:         [ pw(-0.29,-0.04, 0.00), pw( 0.29,-0.04, 0.00) ],  // lateral trochanter
  hip_flexor_strain:    [ pw(-0.19, 0.02, 0.13), pw( 0.19, 0.02, 0.13) ],  // front/upper thigh
  gluteal_tendinopathy: [ pw(-0.25,-0.05,-0.13), pw( 0.25,-0.05,-0.13) ],  // posterior
  hip_oa:               [ pw(-0.23,-0.04, 0.06), pw( 0.23,-0.04, 0.06) ],
  post_hip_replacement: [ pw(-0.23,-0.04, 0.06), pw( 0.23,-0.04, 0.06) ],
  hip_pointer:          [ pw(-0.29, 0.05, 0.00), pw( 0.29, 0.05, 0.00) ],  // iliac crest

  //  ANKLE / FOOT  (world Y≈-1.30, leg center X≈±0.20, outer X≈±0.26) 
  ankle_sprain_mild:    [ pw(-0.22,-1.30, 0.00), pw( 0.22,-1.30, 0.00) ],  // lateral ankle
  ankle_sprain_severe:  [ pw(-0.22,-1.30, 0.00), pw( 0.22,-1.30, 0.00) ],
  achilles_tendinitis:  [ pw(-0.13,-1.28,-0.14), pw( 0.13,-1.28,-0.14) ],  // back of heel
  achilles_rupture:     [ pw(-0.13,-1.25,-0.14), pw( 0.13,-1.25,-0.14) ],
  plantar_fasciitis:    [ pw(-0.12,-1.46, 0.08), pw( 0.12,-1.46, 0.08) ],  // sole of foot
  pttd:                 [ pw(-0.15,-1.30,-0.04), pw( 0.15,-1.30,-0.04) ],  // medial ankle
  peroneal_tendinitis:  [ pw(-0.23,-1.28, 0.00), pw( 0.23,-1.28, 0.00) ],  // lateral ankle
  ankle_impingement:    [ pw(-0.20,-1.30, 0.09), pw( 0.20,-1.30, 0.09) ],  // front of ankle
  stress_fracture_foot: [ pw(-0.15,-1.38, 0.07), pw( 0.15,-1.38, 0.07) ],  // mid-foot
  mortons_neuroma:      [ pw(-0.12,-1.44, 0.11), pw( 0.12,-1.44, 0.11) ],  // forefoot

  //  ELBOW  (world Y≈+0.02, arm center X≈±0.53, outer X≈±0.57) 
  tennis_elbow:  [ pw(-0.55, 0.02, 0.04), pw( 0.55, 0.02, 0.04) ],  // lateral epicondyle (outer)
  golfers_elbow: [ pw(-0.49, 0.02,-0.03), pw( 0.49, 0.02,-0.03) ],  // medial epicondyle (inner)
  ulnar_nerve:   [ pw(-0.49, 0.02,-0.04), pw( 0.49, 0.02,-0.04) ],
  elbow_bursitis:[ pw(-0.53, 0.02,-0.04), pw( 0.53, 0.02,-0.04) ],  // posterior olecranon
  cubital_tunnel:[ pw(-0.49, 0.02,-0.04), pw( 0.49, 0.02,-0.04) ],

  //  WRIST / HAND  (world Y≈-0.27, arm center X≈±0.52) 
  carpal_tunnel:  [ pw(-0.52,-0.27, 0.04), pw( 0.52,-0.27, 0.04) ],  // front of wrist
  wrist_sprain:   [ pw(-0.52,-0.26, 0.02), pw( 0.52,-0.26, 0.02) ],
  de_quervain:    [ pw(-0.48,-0.22, 0.04), pw( 0.48,-0.22, 0.04) ],  // radial (thumb) side
  trigger_finger: [ pw(-0.52,-0.31, 0.05), pw( 0.52,-0.31, 0.05) ],  // palm
  dupuytren:      [ pw(-0.52,-0.31, 0.05), pw( 0.52,-0.31, 0.05) ],
  crps:           [ pw(-0.52,-0.27, 0.03), pw( 0.52,-0.27, 0.03) ],

  //  UPPER / MID BACK  (world Y≈0.40–0.75) 
  thoracic_herniated_disc: [ pw( 0.00, 0.40,-0.13) ],
  postural_dysfunction:    [ pw( 0.00, 0.55,-0.13) ],
  kyphosis:                [ pw( 0.00, 0.46,-0.15) ],
  rib_stress_fracture:     [ pw(-0.19, 0.55,-0.04), pw( 0.19, 0.55,-0.04) ],
  costochondritis:         [ pw(-0.16, 0.58, 0.15), pw( 0.16, 0.58, 0.15) ],  // front of chest

  //  ABDOMEN 
  sports_hernia:   [ pw(-0.09, 0.10, 0.15), pw( 0.09, 0.10, 0.15) ],  // lower abdomen
  pelvic_floor:    [ pw( 0.00,-0.02, 0.09) ],
  diastasis_recti: [ pw( 0.00, 0.26, 0.15) ],  // midline abdomen

  //  HEAD / NEUROLOGICAL 
  concussion:           [ pw( 0.00, 1.36, 0.04) ],
  vestibular_disorder:  [ pw(-0.11, 1.32, 0.05), pw( 0.11, 1.32, 0.05) ],  // inner ear
  tmj:                  [ pw(-0.17, 1.24, 0.13), pw( 0.17, 1.24, 0.13) ],  // jaw joint
  stroke_rehab:         [ pw( 0.00, 1.36, 0.02) ],
  parkinsons:           [ pw( 0.00, 1.36, 0.02) ],
  brachial_plexus:      [ pw(-0.15, 1.00, 0.02), pw( 0.15, 1.00, 0.02) ],  // neck-shoulder
  peripheral_neuropathy:[ pw(-0.11,-1.32, 0.05), pw( 0.11,-1.32, 0.05) ],  // feet
}

//  Camera positions in scene space 
const DEFAULT_CAM = { pos:[0, 0.1, 5.8], target:[0, 0.1, 0] }
// Camera positions all stay on +Z side. Model rotation handles showing front/back/side.
const REGION_CAM = {
  'Head / Neck':        { pos:[ 0.0,  1.26, 2.2],  target:[ 0,    1.26, 0], back:false },
  'Left Shoulder':      { pos:[-0.85, 0.82, 2.4],  target:[-0.35, 0.82, 0], back:false },
  'Right Shoulder':     { pos:[ 0.85, 0.82, 2.4],  target:[ 0.35, 0.82, 0], back:false },
  'Chest / Ribs':       { pos:[ 0.0,  0.60, 2.5],  target:[ 0,    0.60, 0], back:false },
  'Upper Back':         { pos:[ 0,    0.82, 2.4],   target:[ 0,    0.82, 0], back:true  },
  'Mid Back':           { pos:[ 0,    0.46, 2.4],   target:[ 0,    0.46, 0], back:true  },
  'Lower Back':         { pos:[ 0,    0.05, 2.4],   target:[ 0,    0.05, 0], back:true  },
  'Abdomen / Groin':    { pos:[ 0,    0.17, 2.5],   target:[ 0,    0.17, 0], back:false },
  'Left Elbow':         { pos:[-1.10, 0.02, 2.2],   target:[-0.70, 0.02, 0], back:false },
  'Right Elbow':        { pos:[ 1.10, 0.02, 2.2],   target:[ 0.70, 0.02, 0], back:false },
  'Left Wrist / Hand':  { pos:[-1.05,-0.27, 1.9],   target:[-0.70,-0.27, 0], back:false },
  'Right Wrist / Hand': { pos:[ 1.05,-0.27, 1.9],   target:[ 0.70,-0.27, 0], back:false },
  'Left Hip':           { pos:[-0.55,-0.05, 2.5],   target:[-0.18,-0.05, 0], back:false },
  'Right Hip':          { pos:[ 0.55,-0.05, 2.5],   target:[ 0.18,-0.05, 0], back:false },
  'Left Knee':          { pos:[-0.52,-0.67, 2.2],   target:[-0.19,-0.67, 0], back:false },
  'Right Knee':         { pos:[ 0.52,-0.67, 2.2],   target:[ 0.19,-0.67, 0], back:false },
  'Left Ankle / Foot':  { pos:[-0.48,-1.30, 1.9],   target:[-0.20,-1.30, 0], back:false },
  'Right Ankle / Foot': { pos:[ 0.48,-1.30, 1.9],   target:[ 0.20,-1.30, 0], back:false },
}

function getCamForRegions(regions) {
  if (!regions || !regions.length) return { ...DEFAULT_CAM, back: false }
  for (const r of regions) {
    if (REGION_CAM[r]) return REGION_CAM[r]
  }
  return { ...DEFAULT_CAM, back: false }
}

function easeInOut(t) { return t < 0.5 ? 2*t*t : -1 + (4 - 2*t)*t }
function lerp(a, b, t) { return a + (b - a) * t }

//  Shortest-path rotation diff (safe for any magnitude of fromY) 
function shortestDiff(from, to) {
  const TWO_PI = 2 * Math.PI
  let d = to - from
  return d - TWO_PI * Math.floor(d / TWO_PI + 0.5)
}




const SKIN_R = 0.831, SKIN_G = 0.647, SKIN_B = 0.533  // warm skin #d4a588
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"


//  Anatomical SVG Illustrations — one per injury category 
function AnatomySVG({ category, color }) {
  const s = { fill: 'none', stroke: color, strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' }
  const sf = { ...s, fill: color, fillOpacity: 0.12 }
  const sd = { ...s, strokeDasharray: '3,2', strokeOpacity: 0.55 }
  const t = { fill: color, fontSize: 7, fontFamily: FONT, fontWeight: '700', opacity: 0.7 }

  if (category === 'Knee') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* Femur shaft */}
      <rect x="72" y="3" width="36" height="22" rx="10" {...sf}/>
      <text x="90" y="14" textAnchor="middle" {...t} fontSize="8">Femur</text>
      {/* Medial & lateral condyles */}
      <ellipse cx="80" cy="30" rx="11" ry="8" {...sf}/>
      <ellipse cx="100" cy="30" rx="11" ry="8" {...sf}/>
      {/* Patella */}
      <ellipse cx="90" cy="24" rx="9" ry="11" {...{...sf, fillOpacity:0.22, strokeWidth:1.8}}/>
      <text x="90" y="27" textAnchor="middle" {...t} fontSize="6">Patella</text>
      {/* Cartilage line */}
      <path d="M68,37 Q90,33 112,37" {...{...s, strokeWidth:3, strokeOpacity:0.2}}/>
      {/* ACL/PCL */}
      <line x1="83" y1="37" x2="97" y2="48" {...sd}/>
      <line x1="97" y1="37" x2="83" y2="48" {...sd}/>
      {/* Tibia */}
      <path d="M70,38 L68,72 L112,72 L110,38 Q90,34 70,38 Z" {...sf}/>
      {/* Fibula */}
      <rect x="113" y="48" width="10" height="24" rx="3" {...s}/>
      <text x="90" y="65" textAnchor="middle" {...t} fontSize="7">Tibia</text>
      {/* Labels */}
      <text x="5" y="30" {...t} fontSize="7">Ligaments</text>
      <path d="M40,30 L67,36" {...{...sd, strokeWidth:1}}/>
    </svg>
  )

  if (category === 'Shoulder') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* Clavicle */}
      <path d="M20,22 Q60,14 90,20" {...{...s, strokeWidth:2.2}}/>
      <text x="45" y="13" textAnchor="middle" {...t} fontSize="7">Clavicle</text>
      {/* Scapula */}
      <path d="M90,20 L100,18 L115,42 L95,55 L82,40 Z" {...sf}/>
      <text x="100" y="54" textAnchor="middle" {...t} fontSize="7">Scapula</text>
      {/* Glenoid socket */}
      <path d="M90,24 Q85,36 90,48" {...{...s, strokeWidth:3, strokeOpacity:0.35}}/>
      {/* Acromion */}
      <path d="M88,18 Q95,15 105,20" {...{...s, strokeWidth:2.5}}/>
      <text x="97" y="12" {...t} fontSize="6">Acromion</text>
      {/* Humeral head (ball) */}
      <circle cx="80" cy="36" r="18" {...sf}/>
      <text x="80" y="40" textAnchor="middle" {...t} fontSize="7">Humerus</text>
      {/* Rotator cuff arc */}
      <path d="M62,24 Q68,16 82,16 Q95,18 102,28" {...{...sd, strokeWidth:1.8}}/>
      <text x="28" y="18" {...t} fontSize="6">Rotator</text>
      <text x="28" y="26" {...t} fontSize="6">Cuff</text>
      {/* Humerus shaft */}
      <rect x="74" y="52" width="12" height="22" rx="4" {...s}/>
    </svg>
  )

  if (category === 'Lower Back') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* 5 lumbar vertebrae */}
      {[0,1,2,3,4].map(i => (
        <g key={i}>
          <rect x="62" y={4 + i*13} width="56" height="10" rx="3" {...sf}/>
          <text x="90" y={11 + i*13} textAnchor="middle" {...t} fontSize="6">L{i+1}</text>
          {/* Disc */}
          {i < 4 && <rect x="67" y={14 + i*13} width="46" height="3" rx="1.5" {...{...s, fill:color, fillOpacity:0.25}}/>}
        </g>
      ))}
      {/* Spinous processes */}
      {[0,1,2,3,4].map(i => (
        <rect key={i} x="88" y={4 + i*13} width="4" height="10" rx="1" {...{...s, strokeOpacity:0}}/>
      ))}
      {/* Sacrum */}
      <path d="M65,70 Q90,74 115,70 L110,63 Q90,67 70,63 Z" {...{...sf, fillOpacity:0.18}}/>
      <text x="90" y="70" textAnchor="middle" {...t} fontSize="6">Sacrum</text>
      {/* Spinal cord line */}
      <line x1="90" y1="4" x2="90" y2="65" {...{...sd, strokeOpacity:0.3}}/>
      {/* Labels */}
      <text x="128" y="30" {...t} fontSize="7">Lumbar</text>
      <text x="128" y="39" {...t} fontSize="7">Spine</text>
      <text x="5" y="30" {...t} fontSize="6">Disc</text>
      <line x1="25" y1="29" x2="65" y2="27" {...{...sd, strokeWidth:1}}/>
    </svg>
  )

  if (category === 'Neck') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* Skull base */}
      <path d="M70,5 Q90,1 110,5 L112,12 Q90,16 68,12 Z" {...{...sf, fillOpacity:0.2}}/>
      <text x="90" y="11" textAnchor="middle" {...t} fontSize="6">Skull Base</text>
      {/* 7 cervical vertebrae */}
      {[0,1,2,3,4,5,6].map(i => (
        <g key={i}>
          <rect x="66" y={14 + i*8} width="48" height="6" rx="2" {...sf}/>
          <text x="90" y={20 + i*8} textAnchor="middle" {...t} fontSize="5.5">C{i+1}</text>
          {/* Disc */}
          {i < 6 && <rect x="70" y={20 + i*8} width="40" height="2" rx="1" {...{...s, fill:color, fillOpacity:0.25}}/>}
        </g>
      ))}
      {/* Spinous processes (little bumps back) */}
      {[0,1,2,3,4,5,6].map(i => (
        <rect key={i} x="113" y={15 + i*8} width="7" height="5" rx="1.5" {...{...sf, fillOpacity:0.15}}/>
      ))}
      {/* Spinal cord */}
      <line x1="90" y1="14" x2="90" y2="69" {...{...sd, strokeOpacity:0.35}}/>
      <text x="130" y="40" {...t} fontSize="7">Cervical</text>
      <text x="130" y="49" {...t} fontSize="7">Spine</text>
    </svg>
  )

  if (category === 'Hip') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* Pelvis outline */}
      <path d="M30,20 Q50,6 90,8 Q130,6 150,20 L145,45 Q120,55 90,52 Q60,55 35,45 Z" {...sf}/>
      <text x="90" y="28" textAnchor="middle" {...t} fontSize="7">Pelvis</text>
      {/* Acetabulum (left) */}
      <circle cx="58" cy="50" r="12" {...{...sf, fillOpacity:0.08}}/>
      {/* Acetabulum (right) */}
      <circle cx="122" cy="50" r="12" {...{...sf, fillOpacity:0.08}}/>
      {/* Femoral head left */}
      <circle cx="52" cy="52" r="10" {...{...sf, fillOpacity:0.22, strokeWidth:1.8}}/>
      <text x="36" y="70" textAnchor="middle" {...t} fontSize="7">Femur</text>
      {/* Femoral head right */}
      <circle cx="128" cy="52" r="10" {...{...sf, fillOpacity:0.22, strokeWidth:1.8}}/>
      {/* Femur shafts */}
      <rect x="46" y="60" width="12" height="14" rx="4" {...s}/>
      <rect x="122" y="60" width="12" height="14" rx="4" {...s}/>
      {/* Greater trochanter */}
      <ellipse cx="44" cy="55" rx="5" ry="3" {...{...sf, fillOpacity:0.3}}/>
      <ellipse cx="136" cy="55" rx="5" ry="3" {...{...sf, fillOpacity:0.3}}/>
      <text x="90" y="48" textAnchor="middle" {...t} fontSize="6">Hip Joints</text>
    </svg>
  )

  if (category === 'Ankle & Foot') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* Tibia */}
      <rect x="78" y="3" width="16" height="28" rx="4" {...sf}/>
      <text x="86" y="16" textAnchor="middle" {...t} fontSize="6">Tibia</text>
      {/* Fibula */}
      <rect x="96" y="5" width="10" height="26" rx="3" {...s}/>
      <text x="101" y="16" textAnchor="middle" {...t} fontSize="5">Fib</text>
      {/* Talus */}
      <ellipse cx="88" cy="36" rx="16" ry="9" {...{...sf, fillOpacity:0.2}}/>
      <text x="88" y="39" textAnchor="middle" {...t} fontSize="6">Talus</text>
      {/* Calcaneus (heel) */}
      <path d="M68,40 Q72,34 82,34" stroke={color} strokeWidth="1.5" fill="none"/>
      <rect x="60" y="40" width="28" height="18" rx="6" {...{...sf, fillOpacity:0.18}}/>
      <text x="74" y="53" textAnchor="middle" {...t} fontSize="5.5">Heel</text>
      {/* Midfoot & toes */}
      <path d="M88,44 L105,44 L115,42 L130,38 L148,36 L162,36 L168,40" {...{...s, strokeWidth:2.5, strokeOpacity:0.3}}/>
      <path d="M88,52 L106,52 L122,48 L142,44 L160,44 L168,44" {...{...sf, fillOpacity:0.12}}/>
      {/* Ankle ligaments */}
      <line x1="68" y1="36" x2="82" y2="32" {...{...sd, strokeOpacity:0.5}}/>
      <text x="42" y="35" {...t} fontSize="6">Ligament</text>
    </svg>
  )

  if (category === 'Elbow & Wrist') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* Humerus */}
      <rect x="78" y="2" width="24" height="22" rx="6" {...sf}/>
      <text x="90" y="14" textAnchor="middle" {...t} fontSize="7">Humerus</text>
      {/* Medial & lateral epicondyle */}
      <ellipse cx="76" cy="26" rx="7" ry="5" {...{...sf, fillOpacity:0.2}}/>
      <ellipse cx="104" cy="26" rx="7" ry="5" {...{...sf, fillOpacity:0.2}}/>
      {/* Elbow joint space */}
      <text x="90" y="29" textAnchor="middle" {...t} fontSize="6">Elbow</text>
      {/* Radius */}
      <rect x="79" y="28" width="14" height="24" rx="4" {...sf}/>
      <text x="86" y="42" textAnchor="middle" {...t} fontSize="6">Radius</text>
      {/* Ulna */}
      <rect x="95" y="28" width="12" height="26" rx="4" {...s}/>
      <text x="101" y="42" textAnchor="middle" {...t} fontSize="5.5">Ulna</text>
      {/* Wrist joint */}
      <rect x="76" y="54" width="38" height="4" rx="2" {...{...s, fill:color, fillOpacity:0.2}}/>
      <text x="90" y="59" textAnchor="middle" {...t} fontSize="6">Wrist</text>
      {/* Carpal bones */}
      {[0,1,2,3].map(i => (
        <rect key={i} x={78 + i*9} y="60" width="8" height="6" rx="2" {...{...sf, fillOpacity:0.18}}/>
      ))}
      <text x="90" y="72" textAnchor="middle" {...t} fontSize="6">Carpal Bones</text>
    </svg>
  )

  if (category === 'Upper & Mid Back') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* 12 thoracic vertebrae */}
      {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => (
        <g key={i}>
          <rect x="72" y={3 + i*5.8} width="36" height="4.5" rx="1.5" {...sf}/>
          {/* Ribs left */}
          <path d={`M72,${5 + i*5.8} Q${35 - i*1.5},${8 + i*5.8} ${28 - i*1},${12 + i*5.8}`}
            {...{...s, strokeOpacity:0.45}}/>
          {/* Ribs right */}
          <path d={`M108,${5 + i*5.8} Q${145 + i*1.5},${8 + i*5.8} ${152 + i*1},${12 + i*5.8}`}
            {...{...s, strokeOpacity:0.45}}/>
          {/* Disc */}
          {i < 11 && <rect x="75" y={7.5 + i*5.8} width="30" height="1.5" rx="0.75" {...{...s, fill:color, fillOpacity:0.25}}/>}
        </g>
      ))}
      <text x="90" y="73" textAnchor="middle" {...t} fontSize="7">Thoracic Spine</text>
    </svg>
  )

  if (category === 'Abdomen & Pelvis') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* Rib cage base */}
      <path d="M50,10 Q60,5 90,4 Q120,5 130,10 L128,28 Q110,35 90,36 Q70,35 52,28 Z" {...{...sf, fillOpacity:0.15}}/>
      <text x="90" y="22" textAnchor="middle" {...t} fontSize="7">Lower Ribcage</text>
      {/* Abdominal wall (vertical muscles) */}
      <rect x="82" y="28" width="9" height="22" rx="3" {...{...sf, fillOpacity:0.25}}/>
      <rect x="89" y="28" width="9" height="22" rx="3" {...{...sf, fillOpacity:0.25}}/>
      <text x="90" y="44" textAnchor="middle" {...t} fontSize="6">Abs</text>
      {/* Linea alba */}
      <line x1="90" y1="28" x2="90" y2="50" {...{...sd, strokeOpacity:0.35}}/>
      {/* Transverse lines */}
      {[0,1,2].map(i => (
        <line key={i} x1="82" y1={34 + i*7} x2="98" y2={34 + i*7} {...{...sd, strokeOpacity:0.35}}/>
      ))}
      {/* Pelvis */}
      <path d="M42,50 Q60,44 90,46 Q120,44 138,50 L134,68 Q110,74 90,72 Q70,74 46,68 Z" {...{...sf, fillOpacity:0.18}}/>
      <text x="90" y="64" textAnchor="middle" {...t} fontSize="7">Pelvis</text>
      {/* ASIS */}
      <circle cx="56" cy="52" r="4" {...{...sf, fillOpacity:0.3}}/>
      <circle cx="124" cy="52" r="4" {...{...sf, fillOpacity:0.3}}/>
    </svg>
  )

  if (category === 'Neurological') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* Spinal column */}
      <rect x="83" y="3" width="14" height="66" rx="4" {...{...sf, fillOpacity:0.12}}/>
      {/* Vertebrae segments */}
      {[0,1,2,3,4,5,6,7,8].map(i => (
        <rect key={i} x="82" y={4 + i*7.5} width="16" height="5.5" rx="1.5" {...{...sf, fillOpacity:0.22, strokeOpacity:0.5}}/>
      ))}
      {/* Spinal cord */}
      <line x1="90" y1="3" x2="90" y2="69" {...{...s, strokeWidth:2.5, strokeOpacity:0.4}}/>
      {/* Nerve roots branching */}
      {[0,1,2,3,4,5,6,7,8].map(i => {
        const y = 6.5 + i * 7.5
        return (
          <g key={i}>
            <line x1="82" y1={y} x2={58 - i*2} y2={y + 6} {...{...s, strokeOpacity:0.5}}/>
            <line x1="98" y1={y} x2={122 + i*2} y2={y + 6} {...{...s, strokeOpacity:0.5}}/>
          </g>
        )
      })}
      <text x="90" y="74" textAnchor="middle" {...t} fontSize="7">Spinal Nerves</text>
      <text x="18" y="30" {...t} fontSize="7">Nerve</text>
      <text x="18" y="39" {...t} fontSize="7">Roots</text>
      <line x1="42" y1="35" x2="60" y2="32" {...{...sd, strokeWidth:1}}/>
    </svg>
  )

  if (category === 'Systemic') return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      {/* Simple body silhouette */}
      {/* Head */}
      <circle cx="90" cy="10" r="9" {...sf}/>
      {/* Neck */}
      <rect x="86" y="18" width="8" height="6" rx="2" {...sf}/>
      {/* Torso */}
      <path d="M68,24 L72,58 L108,58 L112,24 Q100,20 90,20 Q80,20 68,24 Z" {...sf}/>
      {/* Arms */}
      <rect x="50" y="24" width="18" height="8" rx="4" {...s} transform="rotate(-15,50,24)"/>
      <rect x="112" y="24" width="18" height="8" rx="4" {...s} transform="rotate(15,130,24)"/>
      {/* Legs */}
      <rect x="74" y="58" width="14" height="16" rx="4" {...sf}/>
      <rect x="92" y="58" width="14" height="16" rx="4" {...sf}/>
      {/* Circulation arrows */}
      <path d="M90,28 Q80,36 90,44 Q100,36 90,28" {...{...sd, strokeOpacity:0.45}}/>
      <text x="90" y="36" textAnchor="middle" {...t} fontSize="5.5">Systemic</text>
      {/* Dots for joints/nodes */}
      {[[68,24],[112,24],[74,58],[106,58],[90,44]].map(([cx,cy],i) => (
        <circle key={i} cx={cx} cy={cy} r="3" {...{...sf, fillOpacity:0.4}}/>
      ))}
      <text x="90" y="72" textAnchor="middle" {...t} fontSize="7">Full Body Condition</text>
    </svg>
  )

  // Default fallback
  return (
    <svg viewBox="0 0 180 75" style={{width:'100%',height:'100%'}}>
      <circle cx="90" cy="37" r="30" {...sf}/>
      <text x="90" y="41" textAnchor="middle" {...t} fontSize="10">{category}</text>
    </svg>
  )
}

//  Recovery stats by tissue layer 
const LAYER_STATS = {
  muscle:   { recovery: '2–8 weeks',   cause: 'Overuse or sudden strain',      pt: 'Strengthen & restore flexibility',   severity: 55, icon: '' },
  ligament: { recovery: '6–16 weeks',  cause: 'Twisting or impact force',       pt: 'Stability training & joint support', severity: 75, icon: '' },
  tendon:   { recovery: '4–12 weeks',  cause: 'Repetitive stress / overload',   pt: 'Eccentric loading & load management',severity: 60, icon: '' },
  bone:     { recovery: '6–16 weeks',  cause: 'Trauma or cumulative stress',    pt: 'Progressive weight-bearing rehab',   severity: 70, icon: '' },
  joint:    { recovery: '8–24 weeks',  cause: 'Wear, injury or inflammation',   pt: 'Mobility, strength & pain control',  severity: 65, icon: '' },
  disc:     { recovery: '8–16 weeks',  cause: 'Compression or age-related wear',pt: 'Core stability & posture correction',severity: 80, icon: '' },
  nerve:    { recovery: '6–12 weeks',  cause: 'Compression or irritation',      pt: 'Decompression & nerve mobility',     severity: 72, icon: '' },
  bursa:    { recovery: '3–8 weeks',   cause: 'Repetitive friction / pressure', pt: 'Load reduction & anti-inflammatory', severity: 45, icon: '' },
}

//  Injury popup overlay 
function InjuryPopup({ injury, onClose }) {
  if (!injury) return null
  const lm   = LAYER_META[injury.layer] || LAYER_META['muscle']
  const stat = LAYER_STATS[injury.layer] || LAYER_STATS['muscle']

  return (
    <div style={{
      position:'absolute', top:'12px', right:'12px', width:'226px',
      background:'white', borderRadius:'18px', overflow:'hidden',
      boxShadow:'0 12px 48px rgba(0,0,0,0.22)', border:`2px solid ${lm.border}`,
      fontFamily:FONT, zIndex:10,
      animation:'popupIn 0.38s cubic-bezier(0.34,1.56,0.64,1)',
    }}>

      {/*  Gradient header  */}
      <div style={{
        background:`linear-gradient(135deg, ${lm.color} 0%, ${lm.color}cc 100%)`,
        padding:'11px 32px 10px 13px',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:'5px', marginBottom:'3px' }}>
          <span style={{ fontSize:'14px' }}>{stat.icon}</span>
          <span style={{ fontSize:'8.5px', fontWeight:'800', color:'rgba(255,255,255,0.88)', textTransform:'uppercase', letterSpacing:'0.10em' }}>
            {lm.label} · {injury.category}
          </span>
        </div>
        <div style={{ fontSize:'14px', fontWeight:'800', color:'white', lineHeight:1.15 }}>
          {injury.name}
        </div>
        {injury.bodyRegions?.[0] && (
          <div style={{ fontSize:'9.5px', color:'rgba(255,255,255,0.78)', marginTop:'3px' }}>
             {injury.bodyRegions.slice(0,2).join(' · ')}
          </div>
        )}
      </div>

      {/*  Recovery timeline bar  */}
      <div style={{ padding:'10px 13px 0', background:lm.light }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'4px' }}>
          <span style={{ fontSize:'9px', fontWeight:'700', color:'#64748b', textTransform:'uppercase', letterSpacing:'0.07em' }}>Recovery</span>
          <span style={{ fontSize:'13px', fontWeight:'800', color:lm.color }}>{stat.recovery}</span>
        </div>
        {/* Progress bar representing typical severity */}
        <div style={{ height:'4px', borderRadius:'99px', background:'rgba(0,0,0,0.08)', overflow:'hidden', marginBottom:'10px' }}>
          <div style={{ height:'100%', width:`${stat.severity}%`, borderRadius:'99px', background:`linear-gradient(90deg, ${lm.color}99, ${lm.color})`, transition:'width 0.8s ease' }} />
        </div>
      </div>

      {/*  Key stats  */}
      <div style={{ padding:'8px 13px 10px', display:'flex', flexDirection:'column', gap:'6px' }}>
        <div style={{ display:'flex', gap:'7px', alignItems:'flex-start' }}>
          <span style={{ fontSize:'11px', flexShrink:0, marginTop:'1px' }}></span>
          <div>
            <div style={{ fontSize:'8px', fontWeight:'700', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>Common cause</div>
            <div style={{ fontSize:'10.5px', color:'#334155', fontWeight:'500', lineHeight:1.35 }}>{stat.cause}</div>
          </div>
        </div>
        <div style={{ display:'flex', gap:'7px', alignItems:'flex-start' }}>
          <span style={{ fontSize:'11px', flexShrink:0, marginTop:'1px' }}></span>
          <div>
            <div style={{ fontSize:'8px', fontWeight:'700', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' }}>PT focus</div>
            <div style={{ fontSize:'10.5px', color:'#334155', fontWeight:'500', lineHeight:1.35 }}>{stat.pt}</div>
          </div>
        </div>

        {/* Tags */}
        {injury.tags?.length > 0 && (
          <div style={{ display:'flex', gap:'3px', flexWrap:'wrap', marginTop:'2px' }}>
            {injury.tags.slice(0,4).map(tag => (
              <span key={tag} style={{
                fontSize:'8px', fontWeight:'600', padding:'2px 6px', borderRadius:'99px',
                background:lm.light, color:lm.color, border:`1px solid ${lm.border}`,
                textTransform:'capitalize',
              }}>{tag}</span>
            ))}
          </div>
        )}

        {/* Caleb nudge */}
        <div style={{
          marginTop:'2px', padding:'6px 9px', borderRadius:'10px',
          background:lm.light, border:`1px solid ${lm.border}`,
          display:'flex', alignItems:'center', gap:'5px',
        }}>
          <span style={{ fontSize:'11px' }}></span>
          <div style={{ fontSize:'9.5px', color:lm.color, fontWeight:'700' }}>
            Full explanation in Caleb's panel →
          </div>
        </div>
      </div>

      {/* Close */}
      <button onClick={onClose} style={{
        position:'absolute', top:'8px', right:'9px', width:'18px', height:'18px',
        background:'rgba(255,255,255,0.25)', border:'none', borderRadius:'50%',
        cursor:'pointer', color:'white', fontSize:'11px', lineHeight:1,
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>×</button>
    </div>
  )
}

//  Main component 
export default function HumanBody3D({
  selectedInjury     = null,
  highlightedRegions = [],
  highlightLayer     = null,
}) {
  const canvasRef     = useRef(null)
  const sceneRef      = useRef(null)
  const tweenRef      = useRef(null)
  const rotTweenRef   = useRef(null)   // smooth model rotation tween
  const lockedRotRef  = useRef(null)   // locked rotation when injury selected
  const autoRotRef    = useRef(true)
  const autoRotYRef   = useRef(0)      // track current auto-rotation angle
  const modelGroupRef = useRef(null)
  const [status,    setStatus]    = useState('loading')
  const [showPopup, setShowPopup] = useState(false)
  const popupTimer  = useRef(null)

  //  Init Three.js scene 
  useEffect(() => {
    let cancelled = false
    const canvas = canvasRef.current
    if (!canvas) return

    getThree().then(THREE => {
      if (cancelled) return

      const W = canvas.clientWidth  || 440
      const H = canvas.clientHeight || 500

      // Transparent renderer — lets our CSS background show through
      const renderer = new THREE.WebGLRenderer({
        canvas,
        antialias: false,
        alpha: true,                   // ← transparent background
        powerPreference: 'high-performance',
      })
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5))
      renderer.setSize(W, H, false)
      renderer.shadowMap.enabled = false
      renderer.outputEncoding    = THREE.sRGBEncoding
      renderer.setClearColor(0x000000, 0)  // fully transparent clear

      // Scene — no background, no fog
      const scene = new THREE.Scene()
      // scene.background intentionally left null → canvas is transparent

      // Camera
      const camera = new THREE.PerspectiveCamera(40, W / H, 0.05, 60)
      const [px, py, pz] = DEFAULT_CAM.pos
      camera.position.set(px, py, pz)
      const camTarget = new THREE.Vector3(...DEFAULT_CAM.target)
      camera.lookAt(camTarget)

      // Lights — balanced for Z-Anatomy detailed muscle model
      scene.add(new THREE.AmbientLight(0xffffff, 0.65))
      const sun = new THREE.DirectionalLight(0xfff0e0, 1.2)
      sun.position.set(2, 5, 4)
      scene.add(sun)
      const fill = new THREE.DirectionalLight(0xc8e8ff, 0.50)
      fill.position.set(-3, 2, 3)
      scene.add(fill)
      const rim = new THREE.DirectionalLight(0x7788bb, 0.30)
      rim.position.set(-2, 2, -4)
      scene.add(rim)

      // Load GLB — surface model
      const loader = new THREE.GLTFLoader()
      loader.load(
        '/models/3d_male.glb',
        (gltf) => {
          if (cancelled) return
          const object = gltf.scene

          // Auto-scale: compute bounding box, fit to ~3 scene units tall
          const box3 = new THREE.Box3().setFromObject(object)
          const size3 = new THREE.Vector3()
          box3.getSize(size3)
          const autoScale = size3.y > 0 ? 3.0 / size3.y : SCALE
          object.scale.setScalar(autoScale)

          // Center vertically
          const center3 = new THREE.Vector3()
          box3.getCenter(center3)
          object.position.y = -(center3.y * autoScale)

          // Collect all meshes
          const meshes = []
          object.traverse(child => { if (child.isMesh) meshes.push(child) })

          scene.add(object)
          modelGroupRef.current = object

          const meshOrigMats = meshes.map(mesh => mesh.material)
          sceneRef.current.meshBBoxes    = meshes.map(mesh => ({ mesh }))
          sceneRef.current.meshOrigMats  = meshOrigMats
          sceneRef.current.autoScale     = autoScale
          sceneRef.current.modelCenter3Y = center3.y
          setStatus('ready')
        },
        undefined,
        (err) => { console.error('[HumanBody3D] GLTF error:', err); setStatus('error') }
      )

      // Render-on-demand — throttle idle auto-rotate to 30fps
      let needsRender = true
      let lastFrameMs = 0
      const IDLE_INTERVAL = 1000 / 30   // 30fps cap during idle

      function animate(ts) {
        const af = requestAnimationFrame(animate)
        if (sceneRef.current) sceneRef.current.animFrame = af

        if (rotTweenRef.current && modelGroupRef.current) {
          const rt  = rotTweenRef.current
          const now = performance.now()
          const t   = Math.min(1, (now - rt.startTime) / rt.duration)
          const et  = easeInOut(t)
          const rotY = lerp(rt.fromY, rt.toY, et)
          modelGroupRef.current.rotation.y = rotY
          autoRotYRef.current = rotY
          if (t >= 1) {
            rotTweenRef.current = null
            // Snap to exact target to prevent float drift
            modelGroupRef.current.rotation.y = rt.toY
            autoRotYRef.current = rt.toY
          }
          needsRender = true
        } else if (autoRotRef.current && modelGroupRef.current) {
          autoRotYRef.current += 0.004
          modelGroupRef.current.rotation.y = autoRotYRef.current
          needsRender = true
        } else if (!autoRotRef.current && modelGroupRef.current && lockedRotRef.current !== null) {
          // Maintain locked rotation — prevents any drift after tween ends
          if (modelGroupRef.current.rotation.y !== lockedRotRef.current) {
            modelGroupRef.current.rotation.y = lockedRotRef.current
            needsRender = true
          }
        }

        if (tweenRef.current) {
          const tw  = tweenRef.current
          const now = performance.now()
          const t   = Math.min(1, (now - tw.startTime) / tw.duration)
          const et  = easeInOut(t)
          camera.position.set(
            lerp(tw.fromPos[0], tw.toPos[0], et),
            lerp(tw.fromPos[1], tw.toPos[1], et),
            lerp(tw.fromPos[2], tw.toPos[2], et),
          )
          camTarget.set(
            lerp(tw.fromTgt[0], tw.toTgt[0], et),
            lerp(tw.fromTgt[1], tw.toTgt[1], et),
            lerp(tw.fromTgt[2], tw.toTgt[2], et),
          )
          if (t >= 1) tweenRef.current = null
          needsRender = true
        }

        // Pulse the glow halos around pain dots
        if (sceneRef.current?.glowDots?.length) {
          const pulse = 1.0 + 0.28 * Math.sin(Date.now() * 0.0045)
          sceneRef.current.glowDots.forEach(g => g.scale.setScalar(pulse))
          needsRender = true
        }

        if (!needsRender) return
        // Throttle to 30fps during idle auto-rotation (no tweens, no glow pulse)
        const isActive = tweenRef.current || rotTweenRef.current
        if (!isActive && ts - lastFrameMs < IDLE_INTERVAL) return
        lastFrameMs = ts
        needsRender = false
        camera.lookAt(camTarget)
        renderer.render(scene, camera)
      }
      const af = requestAnimationFrame(animate)

      sceneRef.current = {
        THREE, scene, camera, renderer, meshes: [], camTarget,
        animFrame: af,
        requestRender: () => { needsRender = true },
      }
    }).catch(err => { console.error('[HumanBody3D]', err); setStatus('error') })

    return () => {
      cancelled = true
      if (sceneRef.current) {
        cancelAnimationFrame(sceneRef.current.animFrame)
        sceneRef.current.renderer.dispose()
      }
    }
  }, [])

  //  Highlight: glowing red dot at injury site 
  // Dots are children of modelGroupRef so they rotate with the model.
  useEffect(() => {
    if (!sceneRef.current) return
    const sc_ = sceneRef.current
    const T   = window.THREE
    if (!T) return

    // Remove previous pain dots from their parent (model group or scene)
    ;(sc_.painDots || []).forEach(d => { if (d.parent) d.parent.remove(d) })
    sc_.painDots = []
    sc_.glowDots = []

    if (!selectedInjury?.id) { sc_.requestRender?.(); return }

    const modelGroup = modelGroupRef.current
    if (!modelGroup) { sc_.requestRender?.(); return }

    const points = PAIN_POINTS[selectedInjury.id]
    if (!points?.length) { sc_.requestRender?.(); return }

    // PAIN_POINTS are defined in WORLD space (model always 3 units tall, Y=0 center).
    // Convert to model-local coords: localX = wx/autoScale, localY = wy/autoScale + center3Y
    const autoScale  = sc_.autoScale     || 0.04401
    const center3Y   = sc_.modelCenter3Y || -9.054
    const invScale   = 1 / autoScale
    const rCore  = 0.040 * invScale
    const rGlow1 = 0.075 * invScale
    const rGlow2 = 0.110 * invScale

    const glowDots = []
    points.forEach(([wx, wy, wz]) => {
      // World → model-local conversion
      const x = wx / autoScale
      const y = wy / autoScale + center3Y
      const z = wz / autoScale
      // Bright core
      const core = new T.Mesh(
        new T.SphereGeometry(rCore, 14, 14),
        new T.MeshBasicMaterial({ color: 0xff1500, depthTest: false, depthWrite: false })
      )
      core.renderOrder = 999
      core.position.set(x, y, z)
      modelGroup.add(core)
      sc_.painDots.push(core)

      // Inner glow halo
      const glow1 = new T.Mesh(
        new T.SphereGeometry(rGlow1, 8, 8),
        new T.MeshBasicMaterial({ color: 0xff3300, transparent: true, opacity: 0.40, depthTest: false, depthWrite: false })
      )
      glow1.renderOrder = 998
      glow1.position.set(x, y, z)
      modelGroup.add(glow1)
      sc_.painDots.push(glow1)
      glowDots.push(glow1)

      // Outer glow halo
      const glow2 = new T.Mesh(
        new T.SphereGeometry(rGlow2, 8, 8),
        new T.MeshBasicMaterial({ color: 0xff6600, transparent: true, opacity: 0.15, depthTest: false, depthWrite: false })
      )
      glow2.renderOrder = 997
      glow2.position.set(x, y, z)
      modelGroup.add(glow2)
      sc_.painDots.push(glow2)
      glowDots.push(glow2)
    })

    sc_.glowDots = glowDots
    sc_.requestRender?.()
  }, [selectedInjury, status])

  //  Camera zoom + model rotation when injury changes 
  useEffect(() => {
    clearTimeout(popupTimer.current)
    setShowPopup(false)
    if (!sceneRef.current) return
    const { camera, camTarget } = sceneRef.current

    if (!selectedInjury) {
      autoRotRef.current = true
      lockedRotRef.current = null   // unlock rotation on deselect
      tweenRef.current = {
        startTime: performance.now(), duration: 1100,
        fromPos: [camera.position.x, camera.position.y, camera.position.z],
        fromTgt: [camTarget.x, camTarget.y, camTarget.z],
        toPos: DEFAULT_CAM.pos, toTgt: DEFAULT_CAM.target,
      }
      return
    }

    autoRotRef.current = false
    const cam = getCamForRegions(selectedInjury.bodyRegions || [])

    //  Smooth rotation: map injury.view → target Y rotation 
    const targetRotY = (() => {
      const v = selectedInjury.view
      if (v === 'back')       return Math.PI
      if (v === 'side_left')  return -Math.PI / 2
      if (v === 'side_right') return  Math.PI / 2
      if (!v && cam.back)     return Math.PI   // fallback to region hint
      return 0
    })()
    const fromY = modelGroupRef.current ? modelGroupRef.current.rotation.y : 0
    // Fixed shortest-path diff — safe for any magnitude of fromY
    const diff = shortestDiff(fromY, targetRotY)
    lockedRotRef.current = targetRotY   // lock final position
    rotTweenRef.current = {
      startTime: performance.now(), duration: 1000,
      fromY, toY: fromY + diff,
    }

    tweenRef.current = {
      startTime: performance.now(), duration: 1700,
      fromPos: [camera.position.x, camera.position.y, camera.position.z],
      fromTgt: [camTarget.x, camTarget.y, camTarget.z],
      toPos: cam.pos, toTgt: cam.target,
    }

    popupTimer.current = setTimeout(() => setShowPopup(true), 2200)
  }, [selectedInjury, status])

  const handleClosePopup = useCallback(() => setShowPopup(false), [])

  return (
    <div style={{ position:'relative', width:'100%', height:'100%', minHeight:'400px' }}>
      <canvas
        ref={canvasRef}
        style={{ width:'100%', height:'100%', display:'block' }}
        width={440} height={500}
      />

      {/* Loading overlay */}
      {status === 'loading' && (
        <div style={{
          position:'absolute', inset:0, display:'flex', flexDirection:'column',
          alignItems:'center', justifyContent:'center',
          background:'rgba(2,14,26,0.82)', backdropFilter:'blur(6px)',
          fontFamily:FONT,
        }}>
          <div style={{ fontSize:'28px', color:'rgba(0,210,160,0.7)', marginBottom:'10px', animation:'spin3d 1.2s linear infinite' }}></div>
          <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'10px' }}>
            Loading model…
          </div>
        </div>
      )}

      {/* Error overlay */}
      {status === 'error' && (
        <div style={{
          position:'absolute', inset:0, display:'flex', flexDirection:'column',
          alignItems:'center', justifyContent:'center',
          background:'rgba(2,14,26,0.85)', fontFamily:FONT, gap:'6px',
        }}>
          <div style={{ fontSize:'20px' }}></div>
          <div style={{ fontSize:'11px', color:'#64748b', textAlign:'center', lineHeight:1.6, padding:'0 20px' }}>
            Could not load 3D model.<br/>Check your connection and refresh.
          </div>
        </div>
      )}

      {/* Idle hint */}
      {status === 'ready' && !selectedInjury && (
        <div style={{
          position:'absolute', bottom:'10px', left:'50%', transform:'translateX(-50%)',
          fontSize:'10px', color:'rgba(148,163,184,0.55)', fontFamily:FONT,
          pointerEvents:'none', whiteSpace:'nowrap',
        }}>
          Select an injury · auto-rotating
        </div>
      )}

      {/* Injury popup */}
      {showPopup && selectedInjury && (
        <InjuryPopup injury={selectedInjury} onClose={handleClosePopup} />
      )}

      <style>{`
        @keyframes spin3d  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes popupIn { from{opacity:0;transform:scale(0.82) translateY(8px)} to{opacity:1;transform:scale(1) translateY(0)} }
      `}</style>
    </div>
  )
}
